import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import {
  getReportSettingsOverview,
  setReportSubscription,
  setReportScheduleForOwner,
  setRestaurantReportEnabled,
  setInventoryGroupReportEnabled,
  setLeakageReportUnitMode,
  defaultReportEnabled,
  previewSubscriptionsByRole,
  applySubscriptionsByRole,
  ownerScheduleDefaults,
  isGroupScopedReportType,
  isSchedulableReportType,
  REPORT_SEND_TIMES,
  LEAKAGE_REPORT_UNIT_MODES,
  type ReportSettingsOwner,
  type ReportSettingsRestaurant,
  type ReportSettingsInventoryGroup,
  type ReportSendTime,
  type LeakageReportUnitMode,
} from '../../../../services/admin/reportsAdminService';
import type { AdminReportType, AdminReportLanguage } from '../../../../services/admin/adminService';
import { MultiSelectFilter } from '../../../ui/MultiSelectFilter';
import { RESTAURANT_ROLES } from '../../../../services/staff/staffService';
import { WEEK_DAYS, WeekDay } from '../../../../services/restaurant/restaurantService';

type TFn = ReturnType<typeof useTranslation>['t'];

/** Settings-tab heading per report type. Every registry type has one so phase 4 can't miss a label. */
const REPORT_TYPE_LABEL_KEYS: Record<AdminReportType, string> = {
  weekly: 'adminTesting.reportSettings.typeWeekly',
  revenue: 'adminTesting.reportSettings.typeRevenue',
  monthly: 'adminTesting.reportSettings.typeMonthly',
  waste: 'adminTesting.reportSettings.typeWaste',
  inventoryLeakage: 'adminTesting.reportSettings.typeInventoryLeakage',
  inventoryOrder: 'adminTesting.reportSettings.typeInventoryOrder',
  inventoryManagement: 'adminTesting.reportSettings.typeInventoryManagement',
  // Labelled but NOT in SETTINGS_REPORT_TYPES yet — the price-alerts report has no settings
  // tab until it becomes schedulable; the key exists so adding the tab is one array entry.
  priceAlerts: 'adminTesting.reportSettings.typePriceAlerts',
};

export const reportTypeLabel = (t: TFn, reportType: AdminReportType): string =>
  t(REPORT_TYPE_LABEL_KEYS[reportType] ?? reportType);

const REPORT_LANGUAGES: AdminReportLanguage[] = ['en', 'nl'];

/**
 * A restaurant's master switch for this type, as the server resolved it. A missing key means the
 * payload was not built for this type, and falls back to the type's own default — which is OFF
 * for a manual-only type, so it can never paint as sending.
 */
const isRestaurantEnabled = (
  restaurant: ReportSettingsRestaurant,
  reportType: AdminReportType
): boolean => restaurant.enabled?.[reportType] ?? defaultReportEnabled(reportType);

/**
 * Everything the COLLAPSED owner line states, so the list can be read without opening a single
 * accordion: what is switched on, how many recipients are ticked, and whether this account sends
 * anything at all (the sort key — accounts that send come first).
 */
interface OwnerSummary {
  owner: ReportSettingsOwner;
  /** Units this tab switches on: restaurants for a scheduled type, inventory groups for a count-triggered one. */
  enabledCount: number;
  totalCount: number;
  /** Recipient toggles that are ON across every restaurant of the account — what expanding would show. */
  recipientsOn: number;
  recipientsTotal: number;
  /** Any unit on. False = nothing this account is set up for goes out, whatever the recipients say. */
  sending: boolean;
}

const summarizeOwner = (
  owner: ReportSettingsOwner,
  reportType: AdminReportType,
  groupScoped: boolean
): OwnerSummary => {
  const allGroups = owner.restaurants.flatMap((r) => r.inventoryGroups ?? []);
  const enabledCount = groupScoped
    ? allGroups.filter((g) => g.enabled).length
    : owner.restaurants.filter((r) => isRestaurantEnabled(r, reportType)).length;
  // Counted per (user, restaurant) pair, because that is the unit a subscription is stored in —
  // the same person at two venues is two rows and two chances to be missing one.
  const recipientsOn = owner.restaurants.reduce(
    (sum, r) => sum + (r.ownerEnabled ? 1 : 0) + r.staff.filter((s) => s.enabled).length,
    0
  );
  const recipientsTotal = owner.restaurants.reduce((sum, r) => sum + 1 + r.staff.length, 0);
  return {
    owner,
    enabledCount,
    totalCount: groupScoped ? allGroups.length : owner.restaurants.length,
    recipientsOn,
    recipientsTotal,
    sending: enabledCount > 0,
  };
};

/** Shared control styling for the owner-level selects. */
const selectClass = cn(
  'px-2 py-1 rounded-md border text-xs',
  'bg-white dark:bg-gray-800',
  'border-gray-200 dark:border-gray-700',
  'text-gray-900 dark:text-gray-100',
  'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500'
);

/**
 * One report type's settings, grouped by owner account: the owner's send day / slot / language
 * (one control, fanned out to every venue on save), each venue's master switch, and each user's
 * subscription.
 */
export const ReportSettingsSection = ({ reportType }: { reportType: AdminReportType }) => {
  /**
   * The two count-triggered types are configured one level deeper — per inventory group — and
   * have no send day at all, so this tab drops the schedule controls, the restaurant master
   * switch and the by-role helper (which can only write restaurant-level rows) and renders a
   * switch + recipient list per group instead.
   */
  const groupScoped = isGroupScopedReportType(reportType);
  /**
   * Does the dispatcher send this type on a send day? Only these types expose day/slot controls.
   */
  const schedulable = isSchedulableReportType(reportType);
  const delayedLeakage = reportType === 'inventoryLeakage';
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or the load
  // effect below re-runs on every render (infinite fetch loop).
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  const [owners, setOwners] = useState<ReportSettingsOwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  /** The bulk-by-role tool is opened deliberately; the settings list is what the tab is for. */
  const [helperOpen, setHelperOpen] = useState(false);
  const [expandedOwnerId, setExpandedOwnerId] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  /**
   * Was each account sending AS LOADED — the FLOOR for which block it sits in, never the ceiling.
   * An account switched off inside an expanded card keeps its place in the top block until the
   * next load, so the card cannot drop out from under the cursor mid-edit; its badges still
   * update immediately. Promotion is not frozen — see {@link sortedOwners}.
   */
  const [sendingAtLoad, setSendingAtLoad] = useState<Record<string, boolean>>({});

  const loadData = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await getReportSettingsOverview(reportType);
      setOwners(data);
      setSendingAtLoad(
        Object.fromEntries(
          data.map((o) => [o.ownerUserId, summarizeOwner(o, reportType, isGroupScopedReportType(reportType)).sending])
        )
      );
    } catch (error) {
      console.error('Failed to load report settings overview:', error);
      // Without this the failure renders as "No admins found", which reads as
      // "nobody is subscribed" — the opposite of what a failed load means.
      setOwners([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  /** Each account's collapsed-line facts, computed once per load rather than per render. */
  const summaries = useMemo(
    () => owners.map((o) => summarizeOwner(o, reportType, groupScoped)),
    [owners, reportType, groupScoped]
  );

  const filteredSummaries = useMemo(() => {
    if (!searchQuery.trim()) return summaries;
    const query = searchQuery.toLowerCase();
    return summaries.filter(
      ({ owner: o }) =>
        o.ownerEmail.toLowerCase().includes(query) ||
        (o.ownerName && o.ownerName.toLowerCase().includes(query)) ||
        o.restaurants.some((r) => r.restaurantName.toLowerCase().includes(query))
    );
  }, [summaries, searchQuery]);

  /**
   * Sending accounts first, then the ones with nothing on — so the top block is the live setup to
   * check and the bottom block the backlog to ignore. `sort` is stable, so each block keeps the
   * server's ordering.
   *
   * Membership is ASYMMETRIC on purpose. The live `sending` PROMOTES on the spot: switching a
   * group (or a restaurant) on is the moment the account starts mailing, and leaving it sat in
   * "nothing on" until a reload reads as if the switch did not take. {@link sendingAtLoad} only
   * ever HOLDS an account in the top block after it is switched off, so a demotion cannot yank
   * the card being edited to the bottom of the page.
   */
  const sortedOwners = useMemo(
    () =>
      filteredSummaries
        .map((summary) => ({
          ...summary,
          block: summary.sending || (sendingAtLoad[summary.owner.ownerUserId] ?? false),
        }))
        .sort((a, b) => Number(b.block) - Number(a.block)),
    [filteredSummaries, sendingAtLoad]
  );

  const sendingBlockTotal = sortedOwners.filter((s) => s.block).length;

  /**
   * Toggle one recipient's subscription AT ONE RESTAURANT. Subscriptions are per
   * (user, restaurant, type), so only that pair may change in local state — the same user under
   * another restaurant keeps its own value. The owner is the restaurant's `ownerEnabled` field.
   */
  const handleToggleUser = async (
    ownerUserId: string,
    userId: string,
    restaurantId: string,
    next: boolean
  ) => {
    setUpdating(`user-${userId}-${restaurantId}`);
    try {
      await setReportSubscription(userId, restaurantId, reportType, next);
      setOwners((prev) =>
        prev.map((o) => {
          if (o.ownerUserId !== ownerUserId) return o;
          return {
            ...o,
            restaurants: o.restaurants.map((r) => {
              if (r.restaurantId !== restaurantId) return r;
              if (userId === o.ownerUserId) return { ...r, ownerEnabled: next };
              return {
                ...r,
                staff: r.staff.map((s) => (s.id === userId ? { ...s, enabled: next } : s)),
              };
            }),
          };
        })
      );
    } catch (error) {
      console.error('Failed to update subscription:', error);
    } finally {
      setUpdating(null);
    }
  };

  /** Which inventory groups produce this report — the group tabs' extra layer. */
  const handleToggleGroupEnabled = async (
    ownerUserId: string,
    restaurantId: string,
    inventoryGroupId: string,
    next: boolean
  ) => {
    setUpdating(`group-${inventoryGroupId}`);
    try {
      await setInventoryGroupReportEnabled(restaurantId, inventoryGroupId, reportType, next);
      setOwners((prev) =>
        prev.map((o) => {
          if (o.ownerUserId !== ownerUserId) return o;
          return {
            ...o,
            restaurants: o.restaurants.map((r) =>
              r.restaurantId === restaurantId
                ? {
                    ...r,
                    inventoryGroups: (r.inventoryGroups ?? []).map((g) =>
                      g.inventoryGroupId === inventoryGroupId ? { ...g, enabled: next } : g
                    ),
                  }
                : r
            ),
          };
        })
      );
    } catch (error) {
      console.error('Failed to update the inventory group switch:', error);
    } finally {
      setUpdating(null);
    }
  };

  /**
   * The unit the LEAKAGE report prints its quantities in, for ONE restaurant. Only reachable on
   * the 'inventoryLeakage' tab; nothing else on this page changes what a report looks like, so it is
   * patched optimistically like every other switch here and reverted by a reload on failure.
   */
  const handleChangeLeakageUnit = async (
    ownerUserId: string,
    restaurantId: string,
    mode: LeakageReportUnitMode
  ) => {
    setUpdating(`leakage-unit-${restaurantId}`);
    try {
      await setLeakageReportUnitMode(restaurantId, mode);
      setOwners((prev) =>
        prev.map((o) => {
          if (o.ownerUserId !== ownerUserId) return o;
          return {
            ...o,
            restaurants: o.restaurants.map((r) =>
              r.restaurantId === restaurantId ? { ...r, leakageUnitMode: mode } : r
            ),
          };
        })
      );
    } catch (error) {
      console.error('Failed to update the leakage unit mode:', error);
    } finally {
      setUpdating(null);
    }
  };

  /**
   * One schedule field at a time, for the whole owner account — the server writes the value to
   * every restaurant that account owns, which is also why `mixed` clears here: after the fan-out
   * no venue can still hold a different value.
   */
  const handleChangeSchedule = async (
    ownerUserId: string,
    changes: { sendDay?: WeekDay; sendTime?: ReportSendTime; language?: AdminReportLanguage }
  ) => {
    setUpdating(`schedule-${ownerUserId}`);
    try {
      await setReportScheduleForOwner(ownerUserId, reportType, changes);
      setOwners((prev) =>
        prev.map((o) => {
          if (o.ownerUserId !== ownerUserId) return o;
          const current = o.schedules?.[reportType] ?? ownerScheduleDefaults(reportType);
          return {
            ...o,
            schedules: { ...o.schedules, [reportType]: { ...current, ...changes, mixed: false } },
          };
        })
      );
    } catch (error) {
      console.error('Failed to update schedule:', error);
    } finally {
      setUpdating(null);
    }
  };

  /** The per-restaurant master switch — the one setting that stays individual. */
  const handleToggleRestaurantEnabled = async (
    ownerUserId: string,
    restaurantId: string,
    next: boolean
  ) => {
    setUpdating(`restaurant-${restaurantId}`);
    try {
      await setRestaurantReportEnabled(restaurantId, reportType, next);
      setOwners((prev) =>
        prev.map((o) => {
          if (o.ownerUserId !== ownerUserId) return o;
          return {
            ...o,
            restaurants: o.restaurants.map((r) =>
              r.restaurantId === restaurantId
                ? { ...r, enabled: { ...r.enabled, [reportType]: next } }
                : r
            ),
          };
        })
      );
    } catch (error) {
      console.error('Failed to update the restaurant switch:', error);
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className={cn(secondaryText)}>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Search + the by-role helper's own toggle — the settings list starts right below. */}
      <div className="mb-4 flex items-start gap-3 flex-wrap">
        <input
          type="text"
          placeholder={t('adminTesting.reportSettings.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={cn(
            'flex-1 min-w-[200px] max-w-md px-4 py-2 rounded-lg border',
            'bg-white dark:bg-gray-800',
            'border-gray-200 dark:border-gray-700',
            'text-gray-900 dark:text-gray-100',
            'placeholder-gray-400 dark:placeholder-textQuaternary-dark',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500 focus:border-transparent',
            smallTextSize
          )}
        />
        {/*
          The by-role helper writes restaurant-level rows, which a group-scoped type never reads —
          showing it there would tick boxes that silently do nothing. Collapsed by default: it is
          an occasional bulk tool, and open it pushed the actual settings below the fold.
        */}
        {!groupScoped && (
          <button
            onClick={() => setHelperOpen((open) => !open)}
            className={cn(
              'px-3 py-2 rounded-lg border flex items-center gap-2',
              'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
              'hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors',
              secondaryText, 'text-xs', semiBoldText
            )}
          >
            {t('adminTesting.reportSettings.roleHelper.title')}
            <svg
              className={cn('w-3.5 h-3.5 transition-transform', helperOpen && 'rotate-180')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>

      {!groupScoped && helperOpen && (
        <RoleDefaultsHelper reportType={reportType} owners={owners} t={t} onApplied={loadData} />
      )}

      <div className="space-y-3">
        {loadError ? (
          <div className={cn(cardClass, 'p-8 text-center')}>
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('adminTesting.reportSettings.loadError')}
            </p>
            <button
              onClick={loadData}
              className={cn(secondaryText, 'text-xs underline mt-2')}
            >
              {t('adminTesting.reportSettings.retry')}
            </button>
          </div>
        ) : sortedOwners.length === 0 ? (
          <div className={cn(cardClass, 'p-8 text-center')}>
            <p className={secondaryText}>{t('adminTesting.reportSettings.noAdmins')}</p>
          </div>
        ) : (
          sortedOwners.map((summary, index) => {
            const { owner, enabledCount, totalCount, recipientsOn, recipientsTotal, sending, block } = summary;
            const isExpanded = expandedOwnerId === owner.ownerUserId;
            const schedule = owner.schedules?.[reportType] ?? ownerScheduleDefaults(reportType);
            const scheduleBusy = updating === `schedule-${owner.ownerUserId}`;
            // First card of each block gets the block heading — the list is one flat sorted array,
            // so the switch from sending to not-sending IS the divider.
            const startsBlock = index === 0 || sortedOwners[index - 1].block !== block;
            return (
              <Fragment key={owner.ownerUserId}>
              {startsBlock && (
                <div className={cn(secondaryText, 'text-xs', semiBoldText, 'uppercase tracking-wide pt-2 first:pt-0')}>
                  {t(
                    block
                      ? 'adminTesting.reportSettings.blockSending'
                      : 'adminTesting.reportSettings.blockNotSending',
                    { count: block ? sendingBlockTotal : sortedOwners.length - sendingBlockTotal }
                  )}
                </div>
              )}
              <div className={cn(cardClass, 'overflow-hidden')}>
                {/* Owner header */}
                <button
                  onClick={() => setExpandedOwnerId(isExpanded ? null : owner.ownerUserId)}
                  className={cn(
                    'w-full px-4 py-3 flex items-center justify-between',
                    'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left'
                  )}
                >
                  {/* min-w-0 + truncate: the badges to the right never shrink, so without it a
                      long account email pushes them off the card. */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={cn(
                        'w-8 h-8 shrink-0 rounded-full flex items-center justify-center',
                        'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
                        smallTextSize,
                        semiBoldText
                      )}
                    >
                      {(owner.ownerName || owner.ownerEmail)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>{owner.ownerName || owner.ownerEmail}</div>
                      <div className={cn(secondaryText, 'text-xs truncate')}>{owner.ownerEmail}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    <span
                      className={cn(
                        badgeClass,
                        enabledCount > 0
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                      )}
                    >
                      {t(
                        groupScoped
                          ? 'adminTesting.reportSettings.enabledGroupsSummary'
                          : 'adminTesting.reportSettings.enabledRestaurantsSummary',
                        { enabled: enabledCount, total: totalCount }
                      )}
                    </span>
                    {/*
                      Recipients ticked, without opening the card. Amber when the account IS
                      sending but nobody is subscribed — that combination mails nothing and is
                      otherwise invisible from the collapsed line.
                    */}
                    <span
                      className={cn(
                        badgeClass,
                        sending && recipientsOn === 0
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                      )}
                    >
                      {t('adminTesting.reportSettings.recipientsSummary', {
                        on: recipientsOn,
                        total: recipientsTotal,
                      })}
                    </span>
                    {/*
                      When this account's reports go out. Day + slot for a schedulable type;
                      the other two have no send day at all, so they name their trigger instead
                      of a time that would be fiction.
                    */}
                    <span className={cn(badgeClass, 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400')}>
                      {schedulable
                        ? t('adminTesting.reportSettings.scheduleBadge', {
                            day: t(`common.weekdaysShort.${schedule.sendDay}`),
                            time: schedule.sendTime,
                          })
                        : t(
                            delayedLeakage
                              ? 'adminTesting.reportSettings.leakageTriggerBadge'
                              : groupScoped
                                ? 'adminTesting.reportSettings.triggerBadge'
                                : 'adminTesting.reportSettings.manualBadge'
                          )}
                    </span>
                    {/* The day above is only the most recent one — say so before it is trusted. */}
                    {schedulable && schedule.mixed && (
                      <span className={cn(badgeClass, 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400')}>
                        {t('adminTesting.reportSettings.mixedBadge')}
                      </span>
                    )}
                    {/* Only adds something on a group-scoped tab: there the badge above counts
                        inventories, so the venue count is a separate fact. Elsewhere it is the
                        badge's own total, and repeating it just crowds the line. */}
                    {groupScoped && (
                      <span className={cn(secondaryText, 'text-xs shrink-0')}>
                        {owner.restaurants.length === 1
                          ? t('adminTesting.reportSettings.restaurantCountOne')
                          : t('adminTesting.reportSettings.restaurantCountOther', { count: owner.restaurants.length })}
                      </span>
                    )}
                    <svg
                      className={cn('w-4 h-4 shrink-0 text-gray-400 dark:text-textQuaternary-dark transition-transform', isExpanded && 'rotate-180')}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Expanded */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                    {/*
                      A count-triggered type has no send day to configure: it goes out when the
                      group's count session is finalized. Say so, rather than showing controls
                      that would never be read.
                    */}
                    {groupScoped ? (
                      <div className="mt-4 rounded-lg border border-gray-100 dark:border-gray-700 p-3">
                        <span className={cn(primaryText, 'text-xs', semiBoldText)}>
                          {t('adminTesting.reportSettings.triggerTitle')}
                        </span>
                        <p className={cn(secondaryText, 'text-xs mt-1')}>
                          {t('adminTesting.reportSettings.triggerHint')}
                        </p>
                      </div>
                    ) : (
                    <div className="mt-4 rounded-lg border border-gray-100 dark:border-gray-700 p-3">
                      {/*
                        A type with no send day AND no count trigger has no automatic send path
                        at all — it exists only as an on-demand send from Send Reports. Say that
                        here, or the switch and recipients below read as a live weekly send.
                        The rows are still written: they are what a future trigger will read.
                      */}
                      {!schedulable && !delayedLeakage && (
                        <p className="mb-3 text-xs text-amber-600 dark:text-amber-400">
                          {t('adminTesting.reportSettings.manualOnlyHint')}
                        </p>
                      )}
                      {reportType === 'waste' && (
                        <p className={cn(secondaryText, 'mb-3 text-xs')}>
                          {t('adminTesting.reportSettings.wasteScheduleHint')}
                        </p>
                      )}
                      {delayedLeakage && (
                        <p className={cn(secondaryText, 'mb-3 text-xs')}>
                          {t('adminTesting.reportSettings.leakageScheduleHint')}
                        </p>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn(primaryText, 'text-xs', semiBoldText)}>
                          {t('adminTesting.reportSettings.ownerScheduleTitle')}
                        </span>
                        {schedule.mixed && (
                          <span className={cn(badgeClass, 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400')}>
                            {t('adminTesting.reportSettings.mixedBadge')}
                          </span>
                        )}
                      </div>
                      <p className={cn(secondaryText, 'text-xs mt-1')}>
                        {schedule.mixed
                          ? t('adminTesting.reportSettings.ownerScheduleMixedHint')
                          : t('adminTesting.reportSettings.ownerScheduleHint', { count: owner.restaurants.length })}
                      </p>

                      <div className="mt-3 flex items-center gap-x-3 gap-y-2 flex-wrap">
                        {/*
                          Day and slot exist only for weekday-scheduled types. Leakage
                          uses its fixed count-date delay instead.
                        */}
                        {schedulable && (
                          <>
                            <span className={cn(secondaryText, 'text-xs')}>
                              {t('adminTesting.reportSettings.sendDayLabel')}
                            </span>
                            <select
                              value={schedule.sendDay}
                              disabled={scheduleBusy || reportType === 'waste'}
                              onChange={(e) =>
                                handleChangeSchedule(owner.ownerUserId, { sendDay: e.target.value as WeekDay })
                              }
                              className={selectClass}
                            >
                              {WEEK_DAYS.map((day) => (
                                <option key={day} value={day}>
                                  {t(`common.weekdays.${day}`)}
                                </option>
                              ))}
                            </select>

                            <span className={cn(secondaryText, 'text-xs')}>
                              {t('adminTesting.reportSettings.sendTimeLabel')}
                            </span>
                            <select
                              value={schedule.sendTime}
                              disabled={scheduleBusy || reportType === 'waste'}
                              onChange={(e) =>
                                handleChangeSchedule(owner.ownerUserId, { sendTime: e.target.value as ReportSendTime })
                              }
                              className={selectClass}
                            >
                              {REPORT_SEND_TIMES.map((time) => (
                                <option key={time} value={time}>
                                  {time}
                                </option>
                              ))}
                            </select>
                          </>
                        )}

                        <span className={cn(secondaryText, 'text-xs')}>
                          {t('adminTesting.reportSettings.languageLabel')}
                        </span>
                        <select
                          value={schedule.language}
                          disabled={scheduleBusy}
                          onChange={(e) =>
                            handleChangeSchedule(owner.ownerUserId, {
                              language: e.target.value as AdminReportLanguage,
                            })
                          }
                          className={selectClass}
                        >
                          {REPORT_LANGUAGES.map((lang) => (
                            <option key={lang} value={lang}>
                              {t(`adminTesting.reportSettings.language_${lang}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    )}

                    {/* Restaurants — each one is its own on/off unit with its own recipient list */}
                    {owner.restaurants.length === 0 ? (
                      <p className={cn(secondaryText, 'text-xs mt-4')}>
                        {t('adminTesting.reportSettings.noRestaurants')}
                      </p>
                    ) : (
                      <div className="mt-3 space-y-3">
                        {owner.restaurants.map((r) => {
                          // Group-scoped tabs: the restaurant is only a heading — every switch
                          // and every recipient lives on its inventory groups.
                          if (groupScoped) {
                            return (
                              <InventoryGroupSettings
                                key={r.restaurantId}
                                restaurant={r}
                                ownerUserId={owner.ownerUserId}
                                ownerName={owner.ownerName}
                                ownerEmail={owner.ownerEmail}
                                updating={updating}
                                t={t}
                                onToggleGroup={(groupId, next) =>
                                  handleToggleGroupEnabled(owner.ownerUserId, r.restaurantId, groupId, next)
                                }
                                onToggleUser={(userId, next) =>
                                  handleToggleUser(owner.ownerUserId, userId, r.restaurantId, next)
                                }
                              />
                            );
                          }
                          const enabled = isRestaurantEnabled(r, reportType);
                          return (
                          <div
                            key={r.restaurantId}
                            className={cn(
                              'rounded-lg border p-3',
                              // Muted when the restaurant sends nothing — its recipients stay editable.
                              enabled
                                ? 'border-gray-100 dark:border-gray-700'
                                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40'
                            )}
                          >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div
                                className={cn(
                                  enabled ? primaryText : secondaryText,
                                  smallTextSize,
                                  semiBoldText
                                )}
                              >
                                {r.restaurantName}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {/*
                                  Leakage only: which unit its QUANTITIES print in. Sits beside
                                  the master switch because it is the other per-restaurant fact
                                  about this report, and no other type reads it.
                                */}
                                {reportType === 'inventoryLeakage' && (
                                  <>
                                    <span className={cn(secondaryText, 'text-xs')}>
                                      {t('adminTesting.reportSettings.leakageUnitLabel')}
                                    </span>
                                    <select
                                      value={r.leakageUnitMode ?? 'std'}
                                      disabled={updating === `leakage-unit-${r.restaurantId}`}
                                      onChange={(e) =>
                                        handleChangeLeakageUnit(
                                          owner.ownerUserId,
                                          r.restaurantId,
                                          e.target.value as LeakageReportUnitMode
                                        )
                                      }
                                      className={selectClass}
                                    >
                                      {LEAKAGE_REPORT_UNIT_MODES.map((mode) => (
                                        <option key={mode} value={mode}>
                                          {t(
                                            mode === 'count'
                                              ? 'adminTesting.reportSettings.leakageUnitCount'
                                              : 'adminTesting.reportSettings.leakageUnitStd'
                                          )}
                                        </option>
                                      ))}
                                    </select>
                                  </>
                                )}
                                <span className={cn(secondaryText, 'text-xs')}>
                                  {t('adminTesting.reportSettings.reportsEnabledLabel')}
                                </span>
                                <Toggle
                                  checked={enabled}
                                  disabled={updating === `restaurant-${r.restaurantId}`}
                                  onChange={(next) =>
                                    handleToggleRestaurantEnabled(owner.ownerUserId, r.restaurantId, next)
                                  }
                                />
                              </div>
                            </div>

                            {/*
                              Only worth saying for a type that WOULD otherwise be sending. On a
                              manual-only tab every restaurant is off by design, so this line would
                              repeat the tab's own note once per venue.
                            */}
                            {!enabled && (schedulable || delayedLeakage) && (
                              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                {t('adminTesting.reportSettings.restaurantOffNote')}
                              </p>
                            )}

                            {/*
                              Recipients for THIS restaurant — owner first, then staff. Every
                              toggle writes one (user, restaurant) row, and stays editable while
                              the restaurant is off so a client can be set up before go-live.
                            */}
                            <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                              <RecipientRow
                                name={owner.ownerName || owner.ownerEmail}
                                email={owner.ownerEmail}
                                badge={<OwnerLabel t={t} />}
                                checked={r.ownerEnabled}
                                disabled={updating === `user-${owner.ownerUserId}-${r.restaurantId}`}
                                onChange={(next) =>
                                  handleToggleUser(owner.ownerUserId, owner.ownerUserId, r.restaurantId, next)
                                }
                              />
                              {r.staff.map((s) => (
                                <RecipientRow
                                  key={s.id}
                                  name={s.name || s.email}
                                  email={s.email}
                                  badge={<RoleLabel role={s.restaurantRole} t={t} />}
                                  checked={s.enabled}
                                  disabled={updating === `user-${s.id}-${r.restaurantId}`}
                                  onChange={(next) =>
                                    handleToggleUser(owner.ownerUserId, s.id, r.restaurantId, next)
                                  }
                                />
                              ))}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
};

/**
 * One restaurant on a count-triggered tab: WHICH of its inventory groups produce this report,
 * then WHO receives it — the same restaurant-level recipient list every other type uses.
 *
 * The two questions are deliberately separate. A group switch is one setting for the whole
 * restaurant, so switching Food off stops that report for everyone at once, and the recipient
 * ticks below stay exactly as they were.
 *
 * The restaurant itself has no master switch here: with per-group switches it would be a
 * second kill switch over the same decision, and only make "why did this not send" harder.
 */
const InventoryGroupSettings = ({
  restaurant,
  ownerUserId,
  ownerName,
  ownerEmail,
  updating,
  t,
  onToggleGroup,
  onToggleUser,
}: {
  restaurant: ReportSettingsRestaurant;
  ownerUserId: string;
  ownerName: string | null;
  ownerEmail: string;
  updating: string | null;
  t: TFn;
  onToggleGroup: (inventoryGroupId: string, next: boolean) => void;
  onToggleUser: (userId: string, next: boolean) => void;
}) => {
  const groups: ReportSettingsInventoryGroup[] = restaurant.inventoryGroups ?? [];
  const anyGroupOn = groups.some((g) => g.enabled);

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        // Muted while no group reports — the recipients stay editable, so a client can be
        // set up before the first group is switched on.
        anyGroupOn
          ? 'border-gray-100 dark:border-gray-700'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40'
      )}
    >
      <div className={cn(anyGroupOn ? primaryText : secondaryText, smallTextSize, semiBoldText)}>
        {restaurant.restaurantName}
      </div>

      {groups.length === 0 ? (
        <p className={cn(secondaryText, 'text-xs mt-2')}>
          {t('adminTesting.reportSettings.noInventoryGroups')}
        </p>
      ) : (
        <div className="mt-2">
          <p className={cn(secondaryText, 'text-xs')}>
            {t('adminTesting.reportSettings.groupsLabel')}
          </p>
          <div className="mt-2 space-y-1.5">
            {groups.map((g) => (
              <div key={g.inventoryGroupId} className="flex items-center justify-between gap-3">
                <span className={cn(g.enabled ? primaryText : secondaryText, 'text-xs')}>
                  {g.groupName}
                </span>
                <Toggle
                  checked={g.enabled}
                  disabled={updating === `group-${g.inventoryGroupId}`}
                  onChange={(next) => onToggleGroup(g.inventoryGroupId, next)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {groups.length > 0 && !anyGroupOn && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          {t('adminTesting.reportSettings.noGroupsOnNote')}
        </p>
      )}

      {/* Recipients for the whole restaurant — they receive every group switched on above. */}
      <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
        <RecipientRow
          name={ownerName || ownerEmail}
          email={ownerEmail}
          badge={<OwnerLabel t={t} />}
          checked={restaurant.ownerEnabled}
          disabled={updating === `user-${ownerUserId}-${restaurant.restaurantId}`}
          onChange={(next) => onToggleUser(ownerUserId, next)}
        />
        {restaurant.staff.map((s) => (
          <RecipientRow
            key={s.id}
            name={s.name || s.email}
            email={s.email}
            badge={<RoleLabel role={s.restaurantRole} t={t} />}
            checked={s.enabled}
            disabled={updating === `user-${s.id}-${restaurant.restaurantId}`}
            onChange={(next) => onToggleUser(s.id, next)}
          />
        ))}
      </div>
    </div>
  );
};

/** Shared shape of the small badge next to a recipient's name. */
const badgeClass = 'px-2 py-0.5 rounded-full text-xs shrink-0';

/**
 * Restaurant role next to a staff member's name. A missing role is called out in red because
 * the role helper above can't reach role-less staff — they'd silently never get the report.
 */
const RoleLabel = ({ role, t }: { role: string | null; t: TFn }) => (
  <span
    className={cn(
      badgeClass,
      role
        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
        : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
    )}
  >
    {role ?? t('adminTesting.reportSettings.noRole')}
  </span>
);

/**
 * Owner badge. This REPLACES {@link RoleLabel} on the owner row — owners carry no
 * restaurant_role, so RoleLabel would brand them with the red "No role" warning that only
 * means something for staff.
 */
const OwnerLabel = ({ t }: { t: TFn }) => (
  <span className={cn(badgeClass, 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400')}>
    {t('adminTesting.reportSettings.ownerLabel')}
  </span>
);

/**
 * One recipient line inside a restaurant: name + badge, email, and that pair's subscription
 * toggle. The vertical padding pairs with the list's `divide-y` so each toggle reads as
 * belonging to the user on its own row — long lists were ambiguous without it.
 */
const RecipientRow = ({
  name,
  email,
  badge,
  checked,
  disabled,
  onChange,
}: {
  name: string;
  email: string;
  badge: ReactNode;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn(primaryText, 'text-xs', semiBoldText)}>{name}</span>
        {badge}
      </div>
      <div className={cn(secondaryText, 'text-xs')}>{email}</div>
    </div>
    <Toggle checked={checked} disabled={disabled} onChange={onChange} />
  </div>
);

/**
 * Picker label for an owner account. The email is ALWAYS shown: account names repeat across
 * clients ("Restaurant Group", a first name), and picking the wrong one here writes live
 * subscriptions for the wrong client. Never falls back to the name alone.
 */
const ownerPickerLabel = (owner: ReportSettingsOwner): string =>
  owner.ownerName ? `${owner.ownerName} · ${owner.ownerEmail}` : owner.ownerEmail;

/** Chip in the role multi-select. */
const roleChipClass = (active: boolean) =>
  cn(
    'px-2 py-0.5 rounded-full border text-xs transition-colors',
    active
      ? 'bg-purple-600 dark:bg-purple-600 border-purple-600 dark:border-purple-600 text-white dark:text-white'
      : cn(
          'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
          'hover:bg-gray-50 dark:hover:bg-gray-700',
          secondaryText
        )
  );

/**
 * One-shot bulk helper: subscribe or unsubscribe every approved staff member holding the picked
 * roles. Always dry-runs first and shows who would change before writing.
 *
 * SCOPED: the write only reaches the restaurants the two pickers resolve to. Nothing selected
 * means every restaurant on this page — which is the demo-filtered overview, so a "to all"
 * apply can never touch a demo account. The scope is always sent as a concrete id list; the
 * server has no wildcard.
 */
const RoleDefaultsHelper = ({
  reportType,
  owners,
  t,
  onApplied,
}: {
  reportType: AdminReportType;
  owners: ReportSettingsOwner[];
  t: TFn;
  onApplied: () => void;
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>([]);
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<string[]>([]);
  const [enable, setEnable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const ownerOptions = useMemo(
    () => owners.map((o) => ({ id: o.ownerUserId, name: ownerPickerLabel(o) })),
    [owners]
  );

  /**
   * Restaurants the admin-account picker leaves selectable, each carrying the owner it
   * belongs to. Labelled with that owner's name AND email because both venue names and account
   * names repeat across clients, and this list spans every account.
   */
  const restaurantOptions = useMemo(() => {
    const scopedOwners = selectedOwnerIds.length
      ? owners.filter((o) => selectedOwnerIds.includes(o.ownerUserId))
      : owners;
    return scopedOwners.flatMap((o) =>
      o.restaurants.map((r) => ({
        id: r.restaurantId,
        name: `${r.restaurantName} — ${ownerPickerLabel(o)}`,
        ownerUserId: o.ownerUserId,
      }))
    );
  }, [owners, selectedOwnerIds]);

  /**
   * What actually gets written. Restaurant picks win; with none picked it is every restaurant
   * of the picked accounts, and with neither picked the whole (demo-filtered) page.
   *
   * Selections are filtered against the current options rather than cleared on owner change:
   * a restaurant that the account picker has since excluded must not silently stay in scope.
   */
  const scoped = useMemo(() => {
    const inScope = selectedRestaurantIds.length
      ? restaurantOptions.filter((o) => selectedRestaurantIds.includes(o.id))
      : restaurantOptions;
    return {
      restaurantIds: inScope.map((o) => o.id),
      ownerCount: new Set(inScope.map((o) => o.ownerUserId)).size,
    };
  }, [restaurantOptions, selectedRestaurantIds]);

  const isScopeNarrowed = selectedOwnerIds.length > 0 || selectedRestaurantIds.length > 0;
  const scopeSummary = isScopeNarrowed
    ? t('adminTesting.reportSettings.roleHelper.scopeSelected', {
        count: scoped.restaurantIds.length,
        owners: scoped.ownerCount,
      })
    : t('adminTesting.reportSettings.roleHelper.scopeAll', { count: scoped.restaurantIds.length });

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
    setMessage(null);
  };

  const handleApply = async () => {
    if (selectedRoles.length === 0 || scoped.restaurantIds.length === 0 || busy) return;
    try {
      setBusy(true);
      setMessage(null);

      // Dry run first — never write before the super admin has seen who is affected.
      const affected = await previewSubscriptionsByRole(reportType, selectedRoles, scoped.restaurantIds, enable);
      if (affected.length === 0) {
        setMessage({ type: 'success', text: t('adminTesting.reportSettings.roleHelper.noMatches') });
        return;
      }

      // Each line carries the restaurant count, so a scoped apply says how many rows it writes
      // per person instead of only how many people it touches.
      const preview = affected
        .map(
          (u) =>
            `• ${u.name || u.email} (${u.email}) — ${u.restaurantRole ?? '—'} · ` +
            t('adminTesting.reportSettings.roleHelper.userRestaurants', { count: u.restaurantCount })
        )
        .join('\n');
      const confirmed = window.confirm(
        t(
          enable
            ? 'adminTesting.reportSettings.roleHelper.confirmEnable'
            : 'adminTesting.reportSettings.roleHelper.confirmDisable',
          {
            count: affected.length,
            roles: selectedRoles.join(', '),
            scope: scopeSummary,
            users: preview,
          }
        )
      );
      if (!confirmed) return;

      const updatedCount = await applySubscriptionsByRole(reportType, selectedRoles, scoped.restaurantIds, enable);
      setMessage({
        type: 'success',
        text: t('adminTesting.reportSettings.roleHelper.applied', { count: updatedCount }),
      });
      onApplied();
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.message || t('adminTesting.reportSettings.roleHelper.error'),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    // No heading: the toggle that opened this panel is the heading.
    <div className={cn(cardClass, 'p-4 mb-4')}>
      <p className={cn(secondaryText, 'text-xs')}>
        {t('adminTesting.reportSettings.roleHelper.description')}
      </p>

      <div className="mt-3 flex flex-wrap gap-1">
        {RESTAURANT_ROLES.map((role) => (
          <button
            key={role}
            type="button"
            onClick={() => toggleRole(role)}
            className={roleChipClass(selectedRoles.includes(role))}
          >
            {role}
          </button>
        ))}
      </div>

      {/* Scope — nothing picked means everything on the page, so the default stays "all". */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MultiSelectFilter
          label={t('adminTesting.reportSettings.roleHelper.ownersLabel')}
          selectedValues={selectedOwnerIds}
          onValuesChange={(values) => { setSelectedOwnerIds(values); setMessage(null); }}
          availableOptions={ownerOptions}
          maxHeight="max-h-[140px]"
          singleColumn
        />
        <MultiSelectFilter
          label={t('adminTesting.reportSettings.roleHelper.restaurantsLabel')}
          selectedValues={selectedRestaurantIds}
          onValuesChange={(values) => { setSelectedRestaurantIds(values); setMessage(null); }}
          availableOptions={restaurantOptions}
          maxHeight="max-h-[140px]"
          singleColumn
        />
      </div>

      {/* What the apply will actually write to — with nothing picked that is the whole page. */}
      <p className={cn(secondaryText, 'text-xs mt-2')}>
        {t('adminTesting.reportSettings.roleHelper.scopeLabel')}:{' '}
        <span className={cn(primaryText, semiBoldText)}>{scopeSummary}</span>
      </p>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <select
          value={enable ? 'enable' : 'disable'}
          onChange={(e) => { setEnable(e.target.value === 'enable'); setMessage(null); }}
          className={selectClass}
        >
          <option value="enable">{t('adminTesting.reportSettings.roleHelper.actionEnable')}</option>
          <option value="disable">{t('adminTesting.reportSettings.roleHelper.actionDisable')}</option>
        </select>

        <button
          onClick={handleApply}
          disabled={selectedRoles.length === 0 || scoped.restaurantIds.length === 0 || busy}
          className={cn(
            'px-4 py-2 rounded-lg transition-colors text-white dark:text-white',
            'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'text-xs', semiBoldText
          )}
        >
          {busy
            ? t('adminTesting.reportSettings.roleHelper.applying')
            : t('adminTesting.reportSettings.roleHelper.apply')}
        </button>
      </div>

      {message && (
        <div
          className={cn(
            'mt-3 p-2 rounded-lg text-xs',
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          )}
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

interface ToggleProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

const Toggle = ({ checked, disabled, onChange }: ToggleProps) => (
  <label className={cn('relative inline-flex items-center cursor-pointer', disabled && 'opacity-50 pointer-events-none')}>
    <input
      type="checkbox"
      checked={checked}
      onChange={() => onChange(!checked)}
      disabled={disabled}
      className="sr-only peer"
    />
    <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600 dark:peer-checked:bg-purple-600"></div>
  </label>
);
